/**
 * Sanitize an untrusted post-login "redirect" target into a safe same-origin
 * relative path, defending against open-redirect (#6325 share-login flow,
 * #7483 auth/confirm `next` param).
 *
 * A value is accepted only when it is a relative path rooted at a single "/"
 * — never a protocol-relative ("//evil.com"), backslash-smuggled ("/\evil"),
 * or absolute ("https://evil.com") URL. Anything else falls back to `fallback`.
 *
 * Per the OWASP Unvalidated Redirects and Forwards Cheat Sheet
 * (https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html)
 * validation also rejects backslashes anywhere (the WHATWG URL parser treats
 * "\" as "/" in special-scheme URLs, so "/\evil.com" navigates like
 * "//evil.com") and ASCII control characters anywhere (the parser strips
 * tab/CR/LF before parsing, so "/\t//evil.com" canonicalizes to
 * "//evil.com"). Values that don't start with "/" — including userinfo
 * tricks like "@evil.com" (→ "https://origin@evil.com"), host-suffix tricks
 * like ".evil.com" (→ "https://origin.evil.com"), scheme-colon payloads
 * ("javascript:…", "https:…"), and percent-encoded variants of any of these
 * ("%2F%2Fevil.com" — percent-decoding never happens before the checks, so
 * an encoded payload simply fails the single-"/" prefix rule) — are all
 * rejected by the prefix check.
 */
export function safeRedirectPath(
  raw: string | null | undefined,
  fallback = '/'
): string {
  if (!raw) return fallback;
  // Must start with exactly one slash — reject "//" and "/\" (both can be
  // coerced into cross-origin navigations by browsers).
  if (!raw.startsWith('/')) return fallback;
  if (raw.startsWith('//') || raw.startsWith('/\\')) return fallback;
  // Reject backslashes anywhere — the WHATWG URL parser normalizes "\" to
  // "/" in special-scheme URLs, so they can smuggle a protocol-relative URL.
  if (raw.includes('\\')) return fallback;
  // Reject ASCII control characters (C0 + DEL) anywhere — the WHATWG URL
  // parser strips tab/newline and trims other C0 controls BEFORE parsing,
  // so "/\t//evil.com" would otherwise canonicalize to "//evil.com".
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(raw)) return fallback;
  // Reject anything a browser might parse as having a scheme/host.
  if (raw.includes('://')) return fallback;
  return raw;
}
